/**
 * SOLID Review
 * S — Strong. One concern: the Null provider of a contracted, asynchronous port.
 * O — Strong. Any contract works; nothing here names one.
 * L — N/A.
 * I — Strong. One function.
 * D — Good. The kernel's contract type and its failure builder.
 */
import { portFailure, type IPortContract } from '@ml3d/kernel';

/** The failure code a socket's fallback answers with — the frontend failure policy's word for "not wired". */
const UNAVAILABLE = 'UNAVAILABLE';

/**
 * THE FALLBACK A FRONTEND SOCKET DEGRADES TO (frontend strict-ports plan, FE1, 2026-10-03). Every op of
 * the contract answers `{ success: false, error: { code: 'UNAVAILABLE' } }`, so a socket the assembly
 * never connected degrades the move that needed it. The rest of the app still renders; a
 * white screen would not. The kernel guards the fallback on the same terms as a real provider, which
 * is why it answers in the contract's own Result shape.
 *
 * The production-wiring test, not this, is what keeps an unconnected socket from shipping: it asserts
 * `assertAssembled` with an empty allowlist.
 *
 * The one cast in the frontend's port code: an object built op by op from the contract cannot be
 * typed op by op. The contract's guard checks what the type cannot.
 */
export function unavailable<T>(contract: IPortContract): T {
  const failure = portFailure(UNAVAILABLE, `${contract.name} is not connected.`);
  return Object.fromEntries(contract.ops.map((op) => [op, async () => failure])) as T;
}
